import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Modal from "@mui/material/Modal";
import {
  Autocomplete,
  FormControl,
  InputAdornment,
  InputLabel,
  Link,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import SplitButton from "./components/SplitButton";
import { calculateNS, sizeOfBasalSludgeTank } from "./utils/calculate";
import CalculateQsModal from "./components/CalculateQsModal";
import fettutskillerTabell from "./assets/fettutskillerTabell.json";



function handleFettutskillerTabell(calculatedNS) {
  const filtered = fettutskillerTabell.filter((item) => {
    let ns = item["NS (L/s)"];
    if (typeof ns === "string") {
      ns = ns.replace("*", "");
      if (ns.includes("-")) {
        const [min] = ns.split(" - ");
        if (
          calculatedNS <= parseFloat(min)
        ) {
          return true;
        }
      } else {
        ns = parseFloat(ns);
      }
    }
    return ns >= calculatedNS;
  });

  const table = (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            {Object.keys(fettutskillerTabell[0]).map((key) => (
              <TableCell key={key}>{key}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>

          {filtered.map((item) => (
            <TableRow key={item["NS (L/s)"]}>
              {Object.keys(item).map((key) => (
                <TableCell key={key}>{item[key]}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return filtered.length > 0 ? table : null;
}

function App() {
  // Modal
  const [open, setOpen] = React.useState(false);
  const [specificQsMethod, setSpecificQsMethod] = React.useState(false); // [0, 1, 2]
  const handleOpen = () => setOpen(true);
  const handleClose = (event) => {
    setOpen(false);
    setSpecificQsMethod(false);
  };

  // Industries
  const industry_options = [
    { label: "Slakteri", id: 1 },
    { label: "Kjøttproduksjon", id: 2 },
    { label: "Hotell", id: 3 },
    { label: "Restaurant", id: 4 },
    { label: "Sykehus", id: 5 },
    { label: "Kantine", id: 6 },
    { label: "Catering", id: 7 },
  ];

  // QS Button
  const optionsQs = [
    "Kjøkkenutstyr",
    "Virksomhet og antall måltider",
    "Slakter og kjøttproduksjon",
  ];
  const buttonQsHandler = () => {
    setSpecificQsMethod(false);
    handleOpen();
  };
  const itemsQsHandler = (event, index) => {
    setSpecificQsMethod(index);
    handleOpen();
  };

  const [Qs, setQs] = React.useState("");
  const [fd, setFd] = React.useState("");
  const [ft, setFt] = React.useState("");
  const [fr, setFr] = React.useState("");

  // Results & Table
  function createRowData(name, value) {
    return { name, value };
  }

  const results = React.useMemo(
    () => [
      createRowData("Nominell Størrelse (NS)", undefined),
      createRowData("Anbefalt BASAL fettutskiller størrelse", undefined),
      createRowData("Minste slamvolum (V)", undefined),
    ],
    []
  );

  // Calculate NS++ live showcase
  if (!isNaN(Qs) && !isNaN(fd) && !isNaN(ft) && !isNaN(fr)) {
    const QsNum = parseFloat(Qs);
    const fdNum = parseFloat(fd);
    const ftNum = parseFloat(ft);
    const frNum = parseFloat(fr);
    const NS = calculateNS(QsNum, fdNum, ftNum, frNum);
    const basal = sizeOfBasalSludgeTank(NS);
    const V = basal * 100;
    console.log(basal);
    results[0].value = !isNaN(NS.toFixed(1)) ? NS.toFixed(0) : "-";
    results[1].value = basal;
    results[2].value = !isNaN(V.toFixed(1)) ? V.toFixed(0) + " L" : "-";
  }

  // Set Qs from calculations
  const handleSetQs = (Qs) => {
    if (!isNaN(parseFloat(Qs))) {
      Qs = parseFloat(Qs).toFixed(1);
    }
    setQs(Qs);
    handleClose();
  };

  return (
    <>
      <div>
        {/** Kundedetaljer */}
        <h3>Kundedetaljer</h3>
        <>
          <Stack spacing={2} direction="row" sx={{ marginBottom: 4 }}>
            <TextField
              type="text"
              id="outlined-basic"
              label="Referanse Navn"
              variant="outlined"
              fullWidth
            />
            <TextField
              type="text"
              id="outlined-basic"
              label="Referanse ID"
              variant="outlined"
              fullWidth
            />
          </Stack>
          <Stack spacing={2} direction="row" sx={{ mb: 4, mt: 2 }}>
            <Autocomplete
              id="input-industry"
              fullWidth
              freeSolo
              options={industry_options.map((option) => option.label)}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" label="Industri" />
              )}
            />
            <TextField
              id="outlined-basic"
              label="Installasjonsted"
              variant="outlined"
              fullWidth
            />
          </Stack>
        </>
      </div>

      {/** Beregning */}
      <div>
        <h3>Beregning</h3>
        <Stack spacing={2} direction="row" sx={{ mb: 4, mt: 2 }}>
          <FormControl fullWidth>
            <InputLabel htmlFor="qs">Maks vannføring - Qs</InputLabel>
            <OutlinedInput
              id="qs"
              endAdornment={<InputAdornment position="end">L/s</InputAdornment>}
              label="Maks vannføring - Qs"
              value={Qs}
              onChange={(e) => setQs(e.target.value.replace(",", "."))}
              error={isNaN(Qs) || Qs < 0}
              type="number"
            />
          </FormControl>
          <Typography
            color="textSecondary"
            fontStyle="italic"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            eller
          </Typography>
          <SplitButton
            name="Beregn Vannføring - Qs"
            items={optionsQs}
            onClickButton={buttonQsHandler}
            onClickItems={itemsQsHandler}
            disabled={Qs !== ""}
          />
        </Stack>

        <Stack spacing={2} direction="row" sx={{ marginBottom: 4 }}>
          <FormControl fullWidth>
            <InputLabel htmlFor="density">Tetthet - ρ (g/cm3)</InputLabel>
            <Select
              id="density"
              // endAdornment={
              //   <InputAdornment position="end">g/cm3</InputAdornment>
              // }
              label="Tetthet - ρ (g/cm3)"
              value={fd}
              defaultValue={undefined}
              onChange={(e) => setFd(e.target.value)}
            >
              <MenuItem value={1}>{"≤ 0,94 g/cm3"}</MenuItem>
              <MenuItem value={1.5}>{"> 0,94 g/cm3"}</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel htmlFor="temperature">
              Temperatur for avløpsvann ved innløp - T
            </InputLabel>
            <Select
              id="temperature"
              label="Temperatur for avløpsvann ved innløp - T"
              value={ft}
              defaultValue={undefined}
              onChange={(e) => setFt(e.target.value)}
            >
              <MenuItem value={1}>{"≤ 60 °C"}</MenuItem>
              <MenuItem value={1.5}>{"> 60 °C"}</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel htmlFor="cleanser">
              Bruk av vaske-/rengjøringsmidler
            </InputLabel>
            <Select
              id="cleanser"
              label="Bruk av vaske-/rengjøringsmidler"
              value={fr}
              defaultValue={undefined}
              onChange={(e) => setFr(e.target.value)}
            >
              <MenuItem value={1}>{"Anvendes aldri"}</MenuItem>
              <MenuItem value={1.3}>{"Anvendes iblant eller alltid"}</MenuItem>
              <MenuItem value={1.5}>
                {"Ved større forbruk, f.eks. sykehus  ≥"}
              </MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </div>

      <div>
        {/** Kundedetaljer */}
        <h3>Resultater</h3>
        <>
          <TableContainer>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableBody>
                {results.map((row) => (
                  <TableRow
                    key={row.name}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    <TableCell component="th" scope="row" variant="head">
                      {row.name}
                    </TableCell>
                    <TableCell align="right">{row.value}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      </div>

      {!isNaN(parseFloat(results[1].value)) && (
        <Box sx={{ marginTop: 10, alignItems: 'center' }}>

            <Button 
              href="/produkter/basal-fettutskiller/" 
              variant="outlined" 
              color="primary" 
              align="center"
              fullWidth
              sx={{  marginBottom: '20px' }}
            >
              Trykk her for å gå til produktsiden til BASAL fettutskiller
            </Button>

          {handleFettutskillerTabell(results[1].value)}
            <Typography sx={{ my: 3, ml:2, fontSize: '0.9rem', color: 'text.secondary', fontStyle: 'italic' }}>
          * Leveres med integrert sandfang (oppgitt i liter)
          </Typography>
        </Box>
      )}

      {/** Modal */}
      <CalculateQsModal
        open={open}
        onClose={handleClose}
        openSpecific={specificQsMethod}
        handleSetQs={handleSetQs}
      />
    </>
  );
}

export default App;
